import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  cn,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuTrigger,
  Text,
  useIsTouchDevice,
} from "@salestrak/ui";
import { useActivityContext } from "./activity-context";
import { useIssueViewCapabilities } from "../issue-view-capabilities";
import { useAppData } from "#app/lib/data";
import { useMemberLabelResolver } from "#app/lib/data/member-directory-provider";
import { setTaskItemChecked } from "#app/components/editor/task-items";
import { DateText } from "#app/components/date-text";
import { EntityTag, EntityGroupTag, OnBehalfLabel, type EntityData } from "#app/components/entity";
import { NoteContent } from "./note-content";
import { NoteComposer } from "./note-composer";
import { useDockedComposer } from "./docked-composer-context";
import { NoteMediaDisplay } from "./note-media-attachments";
import {
  MediaLightbox,
  type LightboxMediaItem,
} from "#app/components/media/media-lightbox";
import {
  mediaIdFromContentUrl,
  mediaIdFromObjectUrl,
  mediaIdsFromHtml,
} from "#app/lib/media-content";
import { mediaKind } from "#app/lib/media-kind";
import { appOrigin } from "#app/lib/utils/entity-hrefs";
import { NoteReactions } from "./note-reactions";
import { DeleteNoteDialog } from "./delete-note-dialog";
import type { NoteNode, ActivityAuthor } from "./activity-types";
import type { Omittable } from "@salestrak/ui";

// 24px action-button row + paddingY="sm" (8px each); pinned so the header
// doesn't reflow when its right side swaps between date and action buttons.
const NOTE_HEADER_HEIGHT = 40;

type NoteItemProps = Omittable<{
  issueId: string;
  noteId: string;
  isParent?: boolean;
  headerExtra?: React.ReactNode;
  onRequestReply?: () => void;
  onExpandThread?: () => void;
  replyInfo?: {
    count: number;
    /** Replies still unread for the viewer — folded threads must not hide
     *  new entries, so the footer says "· N new" until it opens. */
    unreadCount?: number;
    authors: ActivityAuthor[];
  } | null;
  showFooterBorder?: boolean;
}>;

export const NoteItem = React.memo(function NoteItem({
  issueId,
  noteId,
  isParent = false,
  headerExtra,
  onRequestReply,
  onExpandThread,
  replyInfo = null,
  showFooterBorder = false,
}: NoteItemProps) {
  const {
    getNote,
    toggleReaction,
    updateNote,
    deleteNote,
    selectedNoteId,
    registerNoteRef,
    markNoteRead,
  } = useActivityContext("NoteItem");
  const { currentUser } = useAppData();
  const resolveMemberLabel = useMemberLabelResolver();
  const note = getNote(noteId);
  // View-only viewers can't comment, so reply / edit (which produce
  // new notes) must be hidden too.
  const { canComment } = useIssueViewCapabilities();
  // Backend-resolved per-viewer flags — mirror the @RequireAccess
  // gate (comment-tier access on the parent issue + ownership OR
  // manage.users). Falling back to false during loading so disabled
  // controls don't briefly flash editable.
  const canEdit = !!note?.viewerCanEdit;
  const canDelete = !!note?.viewerCanDelete;
  const isHighlighted = selectedNoteId === noteId;

  const elRef = useRef<HTMLDivElement | null>(null);
  const cardRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      elRef.current = el;
      registerNoteRef(noteId, el);
    },
    [noteId, registerNoteRef],
  );

  // Slack-style: mark this note read once it actually scrolls into view.
  // Only unread notes are observed; the observer fires once then disconnects.
  //
  // We can't use a ratio threshold (e.g. 0.5): a note taller than ~2x the
  // viewport can never reach 50% visibility and would never be marked read.
  // So the trigger stays geometric (threshold 0 + a negative rootMargin) but
  // shrinks ONLY the top — a note counts as "seen" once it clears the top 30%
  // of the viewport. A symmetric band (shrinking the bottom too) leaves a dead
  // zone at the bottom edge, which is exactly where this bottom-anchored feed
  // pins the newest — and most-likely-unread — notes: they sit below the band
  // on open and never get marked unless the user happens to scroll them up.
  // Excluding only the top still ignores an older note briefly peeking in from
  // the top while you read at the bottom. IntersectionObserver already clips
  // against ancestor scroll containers, so a note scrolled out of an overflow
  // region (e.g. a mobile drawer) does not register even with the viewport as
  // root.
  const isUnread = !!note?.unread;
  useEffect(() => {
    if (!isUnread) return;
    const el = elRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          markNoteRead(noteId);
          observer.disconnect();
        }
      },
      { threshold: 0, rootMargin: "-30% 0px 0px 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isUnread, noteId, markNoteRead]);

  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Fullscreen viewer over every image in the note — inline (document order)
  // then footer attachments — opened at whichever one was clicked.
  const [lightbox, setLightbox] = useState<{
    media: LightboxMediaItem[];
    index: number;
  } | null>(null);

  const isTouch = useIsTouchDevice();
  const showActions = isHovered || isMenuOpen;

  const handleToggleReaction = useCallback(
    (emoji: string) => {
      toggleReaction(noteId, emoji);
    },
    [toggleReaction, noteId],
  );

  const handleDelete = useCallback(async () => {
    await deleteNote(noteId);
  }, [deleteNote, noteId]);

  // In the phone sheet an edit is written in the bar on the keyboard, not
  // in the card, whose Save would sink under the keys.
  const dock = useDockedComposer();
  const noteContent = note?.note ?? "";
  const handleEdit = useCallback(() => {
    if (dock) dock.edit(noteId, noteContent);
    else setIsEditing(true);
  }, [dock, noteId, noteContent]);

  // A checklist tick is an edit of the body, so only a viewer who may edit
  // the comment can tick; the boxes are read-only for everyone else.
  // Ticks build on the last body sent, so a second tick made before the
  // first save returns does not undo it.
  const taskBodyRef = useRef(noteContent);
  useEffect(() => {
    taskBodyRef.current = noteContent;
  }, [noteContent]);
  const handleTaskToggle = useCallback(
    async (index: number, checked: boolean) => {
      const base = taskBodyRef.current;
      if (!base) return false;
      const next = setTaskItemChecked(base, index, checked);
      if (next === base) return false;
      taskBodyRef.current = next;
      const saved = !!(await updateNote(noteId, next));
      if (!saved && taskBodyRef.current === next) taskBodyRef.current = base;
      return saved;
    },
    [noteId, updateNote],
  );

  if (!note) return null;

  const author = note.author;
  const currentUserId = currentUser?.userId ?? null;
  const footerMedia = note.media ?? [];
  // An edit by someone other than the author (an admin) names the editor.
  const editorName =
    note.editedBy && note.editedBy !== note.createdBy
      ? resolveMemberLabel(note.editedBy)?.trim() || "another member"
      : null;
  const date = (
    <DateText
      date={note.creationDate}
      format="note"
      size="xs"
      truncate={false}
      suffix={
        note.edited
          ? editorName
            ? ` (edited by ${editorName})`
            : " (edited)"
          : undefined
      }
    />
  );
  const menuTrigger = (
    <MenuTrigger
      render={<Button variant="ghost" size="icon-xs" shape="square" aria-label="Comment actions" />}
    >
      <Icon name="ellipsis" size="xs" />
    </MenuTrigger>
  );
  const deleteItem = (
    <MenuItem onClick={() => setShowDeleteDialog(true)}>
      <Icon name="trash" />
      {isParent ? "Delete thread..." : "Delete comment..."}
    </MenuItem>
  );
  const openLightbox = (imageId: string) => {
    // Inline note media are always images; footer media may be video/pdf/doc
    // (those tiles have their own click actions), so only images join.
    const images: LightboxMediaItem[] = [
      ...mediaIdsFromHtml(note.note).map((id) => ({ id, contentType: "image/*" })),
      ...footerMedia
        .filter((m) => mediaKind(m.contentType) === "image")
        .map((m) => ({ id: m.id, contentType: m.contentType, fileName: m.fileName })),
    ];
    if (!images.length) return;
    setLightbox({
      media: images,
      index: Math.max(0, images.findIndex((m) => m.id === imageId)),
    });
  };
  const handleInlineImageClick = (src: string) => {
    // Rendered inline imgs carry session object URLs; unresolved ones carry
    // the canonical content-route src — which the DOM absolutizes against
    // the APP origin, so also try the bare pathname.
    const photoId =
      mediaIdFromObjectUrl(src) ??
      mediaIdFromContentUrl(src) ??
      mediaIdFromContentUrl(new URL(src, appOrigin()).pathname);
    if (!photoId) return;
    openLightbox(photoId);
  };

  return (
    <>
      <Box ref={cardRefCallback}>
        <Card
          className={cn(
            "overflow-hidden",
            // Square bottom while the thread continues below — the reply
            // region reads as the same card. (A per-side override the
            // `radius` prop cannot express; the seam hides the ring edge.)
            isParent && !showFooterBorder && "rounded-b-none",
            isHighlighted && "border-primary",
          )}
          // Mouse only: a tap's pointerenter would swap the header mid-tap, and
          // iOS then treats the tap as a hover and never sends the click.
          onPointerEnter={(e) => e.pointerType === "mouse" && setIsHovered(true)}
          onPointerLeave={(e) => e.pointerType === "mouse" && setIsHovered(false)}
        >
          <Box
            direction="row"
            align="center"
            justify="between"
            paddingX="md"
            paddingY="sm"
            height={NOTE_HEADER_HEIGHT}
          >
            {note.onBehalf && author ? (
              <Box direction="row" align="center" gap="sm" shrink minWidth={0}>
                <EntityTag
                  entity={{ kind: "user", id: "system", user: null }}
                  display="visual"
                  size="sm"
                  system
                />
                <OnBehalfLabel user={author} />
              </Box>
            ) : (
              <Box direction="row" align="center" shrink>
                <EntityTag
                  entity={{
                    kind: "user",
                    id: author?.userId ?? "system",
                    user: author ?? null,
                  }}
                  display="full"
                  size="sm"
                  weight="semibold"
                  system={!author}
                />
              </Box>
            )}

            {!isEditing && (
              <Box direction="row" align="center" gap="xs" height={24}>
                {isTouch ? (
                  // No hover on touch: the date stays, and the actions fold into the menu.
                  <>
                    {date}
                    {(canEdit || canDelete || (isParent && canComment)) && (
                      <Menu onOpenChange={setIsMenuOpen}>
                        {menuTrigger}
                        <MenuPopup align="end">
                          {canEdit && (
                            <MenuItem onClick={handleEdit}>
                              <Icon name="pen-line" />
                              Edit
                            </MenuItem>
                          )}
                          {isParent && canComment && (
                            <MenuItem onClick={onRequestReply}>
                              <Icon name="reply" />
                              Reply
                            </MenuItem>
                          )}
                          {canDelete && deleteItem}
                        </MenuPopup>
                      </Menu>
                    )}
                  </>
                ) : showActions ? (
                  <>
                    {canEdit && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        shape="square"
                        onClick={handleEdit}
                        aria-label="Edit comment"
                      >
                        <Icon name="pen-line" size="xs" />
                      </Button>
                    )}
                    {isParent && canComment && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        shape="square"
                        onClick={onRequestReply}
                        // The thread's composer sends with "Reply"; this
                        // glyph opens the thread, so its name differs.
                        aria-label="Reply to comment"
                      >
                        <Icon name="reply" size="xs" />
                      </Button>
                    )}
                    {canDelete && (
                      <Menu onOpenChange={setIsMenuOpen}>
                        {menuTrigger}
                        <MenuPopup align="end">{deleteItem}</MenuPopup>
                      </Menu>
                    )}
                  </>
                ) : (
                  date
                )}
              </Box>
            )}

            {isEditing && (
              <DateText date={note.creationDate} format="note" size="xs" truncate={false} />
            )}
          </Box>

          {headerExtra && (
            <Box paddingX="md" paddingBottom="sm">
              {headerExtra}
            </Box>
          )}

          {/* One persistent body box for read + edit with identical padding in
              both states, so entering edit only fades the border in (no layout
              shift) — the transparent border reserves the 1px either way. */}
          <Box
            marginX="sm" marginBottom="sm" transition="colors" duration={200} rounded="md"
            borderColor={isEditing ? "input" : "transparent"}
            paddingX="sm"
            paddingY="2xs"
            className="ease-in-out"
          >
            {isEditing ? (
              <NoteComposer
                issueId={issueId}
                editMode
                editNoteId={noteId}
                initialContent={note.note}
                initialMedia={footerMedia}
                onCancel={() => setIsEditing(false)}
              />
            ) : (
              <>
                <NoteContent
                  content={note.note}
                  onImageClick={handleInlineImageClick}
                  onTaskToggle={canEdit ? handleTaskToggle : undefined}
                />
                {footerMedia.length > 0 ? (
                  <Box paddingBottom="2xs">
                    <NoteMediaDisplay
                      media={footerMedia}
                      onImageClick={openLightbox}
                    />
                  </Box>
                ) : null}
                <MediaLightbox
                  media={lightbox?.media ?? []}
                  open={!!lightbox}
                  onOpenChange={(open) => {
                    if (!open) setLightbox(null);
                  }}
                  initialIndex={lightbox?.index ?? 0}
                />
              </>
            )}
          </Box>

          {/* Footer hosts reactions + the reply CTA. Hide it
              entirely when there's nothing to render — i.e. the
              viewer can't comment AND no existing reactions AND no
              replies to expand. Otherwise even an empty footer adds
              a dead bar below the note. */}
          {(() => {
            if (isEditing) return null;
            const hasReactions = (note.reactions?.length ?? 0) > 0;
            const showReactions = canComment || hasReactions;
            const showReplyCta = !!replyInfo && (canComment || replyInfo.count > 0);
            if (!showReactions && !showReplyCta) return null;
            return (
            <>
              {showFooterBorder && (
                <Box background="border" height={1} />
              )}
              <Box
                direction="row"
                align="center"
                justify="between"
                paddingX="md"
                paddingTop="sm"
                paddingBottom="sm"
              >
                {showReactions ? (
                  <NoteReactions
                    reactions={note.reactions ?? []}
                    currentUserId={currentUserId}
                    onToggleReaction={handleToggleReaction}
                    readOnly={!canComment}
                  />
                ) : (
                  <Box />
                )}

                {/* Reply CTA only shows when the viewer can post.
                    When there ARE replies, keep the count visible so
                    view-only viewers can still expand to read. */}
                {showReplyCta && replyInfo && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={replyInfo.count === 0 ? (onRequestReply ?? onExpandThread) : onExpandThread}
                  >
                    <Text size="xs" variant="muted" weight="medium">
                      {replyInfo.count === 0
                        ? "Reply"
                        : `${replyInfo.count} ${replyInfo.count === 1 ? "reply" : "replies"}`}
                    </Text>
                    {(replyInfo.unreadCount ?? 0) > 0 && (
                      <Text size="xs" variant="primary" weight="medium">
                        {`· ${replyInfo.unreadCount} new`}
                      </Text>
                    )}
                    {replyInfo.count > 0 && replyInfo.authors.length > 0 && (
                      <EntityGroupTag
                        entities={replyInfo.authors.map(
                          (a, i): EntityData => ({
                            kind: "user",
                            id: a.userId ?? String(i),
                            user: a,
                          }),
                        )}
                        max={3}
                        size="xs"
                        title="Replies"
                        interactive={false}
                      />
                    )}
                  </Button>
                )}
              </Box>
            </>
            );
          })()}
        </Card>
      </Box>

      <DeleteNoteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        isParent={isParent}
        replyCount={(note as NoteNode).replyCount ?? 0}
        onConfirm={handleDelete}
      />
    </>
  );
});
